import { useState, useMemo, useCallback, useEffect } from 'react';
import { Drawer, Collapse, Table, Spin, Select, Button } from 'antd';
import { CloseOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import styled from '@emotion/styled';
import { tradePromoOptimizationUrls } from 'config/urls';
import { colorConstants } from 'config/colorConstants';
import { OPTIMIZATION_POLLING_INTERVAL, VALIDATE_PROMO_LIBRARY_POLLING_INTERVAL } from 'constants/tpo';
import { ValidatePromoLibraryPollingStatus } from 'types/tradePromoOptimization';
import type {
    ValidatePromoLibraryResponse,
    ValidationSection,
    ValidationContentBlock,
    ValidationColumn,
} from 'types/tpo/validatePromoLibrary';
import type { ColumnsType } from 'antd/es/table';

const { Panel } = Collapse;

const StyledTable = styled(Table)`
    background: white;
    border-radius: 8px;

    .ant-table {
        background: transparent;
    }

    .ant-table-thead > tr > th {
        background: #f0f0f0 !important;
        font-weight: 500;
        font-size: 13px;
        color: #4e4c55;
        border-bottom: 1px solid #f5f5f5;
        padding: 10px 12px;
        white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
        font-size: 13px;
        padding: 10px 12px;
        border-bottom: 1px solid #f5f5f5;
    }

    .ant-table-tbody > tr:last-child > td {
        border-bottom: none;
    }

    .ant-table-tbody > tr:hover > td {
        background: #fafafa;
    }
`;

interface ValidateLibraryDrawerProps {
    open: boolean;
    onClose: () => void;
}

const FLAG_COLORS = {
    red: '#ef4444',
    green: '#22c55e',
};

const FlagDot = ({ flag }: { flag: 'red' | 'green' }) => (
    <span
        style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: FLAG_COLORS[flag],
            marginRight: 8,
            flexShrink: 0,
        }}
    />
);

const buildTableColumns = (columns: ValidationColumn[]): ColumnsType<Record<string, any>> =>
    columns.map((col, idx) => ({
        title: col.label,
        dataIndex: col.key,
        key: col.key,
        width: idx === 0 ? 300 : 150,
        align: (col.align ?? 'left') as 'left' | 'center' | 'right',
        render: (value: any, row: Record<string, any>) => {
            const flagKey = `${col.key}_flag`;
            const cellFlag = row[flagKey] as 'red' | 'green' | undefined;

            if (cellFlag) {
                return <span style={{ color: FLAG_COLORS[cellFlag], fontWeight: 600 }}>{value ?? '-'}</span>;
            }

            if (col.has_flag && value !== null && value !== undefined && value === 0) {
                return <span style={{ color: FLAG_COLORS.red, fontWeight: 600 }}>{value}</span>;
            }

            return <span>{value ?? '-'}</span>;
        },
    }));

const ContentBlockRenderer = ({ block }: { block: ValidationContentBlock }) => {
    if (block.content_type === 'table' && block.columns && block.rows) {
        const antColumns = buildTableColumns(block.columns);
        return (
            <div className="mb-3 last:mb-0">
                {block.content_title && (
                    <div className="text-sm font-medium text-gray-600 mb-2">{block.content_title}</div>
                )}
                <StyledTable
                    columns={antColumns as ColumnsType<any>}
                    dataSource={block.rows.map((row, idx) => ({ ...row, key: idx }))}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                />
            </div>
        );
    }

    if (block.content_type === 'accordion' && block.items) {
        return (
            <div className="mb-3 last:mb-0">
                {block.content_title && (
                    <div className="text-sm font-medium text-gray-600 mb-2">{block.content_title}</div>
                )}
                <Collapse bordered={false} expandIconPosition="end" style={{ background: 'transparent' }}>
                    {block.items.map((item, idx) => (
                        <Panel
                            key={idx}
                            header={
                                <div className="flex items-center gap-2">
                                    {item.flag && <FlagDot flag={item.flag} />}
                                    <span className="text-sm">{item.title}</span>
                                </div>
                            }
                            style={{ marginBottom: 4, background: 'white', borderRadius: 6, border: 'none' }}
                        >
                            {item.content_type === 'table' && item.columns && item.rows ? (
                                <StyledTable
                                    columns={buildTableColumns(item.columns) as ColumnsType<any>}
                                    dataSource={item.rows.map((row, i) => ({ ...row, key: i }))}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 'max-content' }}
                                />
                            ) : (
                                <div className="text-sm text-gray-600">{item.text ?? ''}</div>
                            )}
                        </Panel>
                    ))}
                </Collapse>
            </div>
        );
    }

    return null;
};

const SectionPanel = ({ section }: { section: ValidationSection }) => (
    <div className="rounded-md p-3">
        {section.content_blocks.map((block) => (
            <ContentBlockRenderer key={block.content_key} block={block} />
        ))}
    </div>
);

const ValidateLibraryDrawer = ({ open, onClose }: ValidateLibraryDrawerProps) => {
    const [selectedCustomer, setSelectedCustomer] = useState<string | undefined>(undefined);
    const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
    const [validationId, setValidationId] = useState<string | null>(null);
    const [pollingStatus, setPollingStatus] = useState<ValidatePromoLibraryPollingStatus>(
        ValidatePromoLibraryPollingStatus.NOT_STARTED,
    );
    const [resultData, setResultData] = useState<ValidatePromoLibraryResponse | null>(null);
    const [expandAll, setExpandAll] = useState(false);
    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    const fetchDropdownValues = useCallback(async (attributeType: string, filters?: Record<string, any>) => {
        const { data } = await axios.post(tradePromoOptimizationUrls.getAttributeNames, {
            library_group: 'promo_library_view',
            attribute_type: attributeType,
            limit: 500,
            offset: 0,
            search_key: '',
            ...(filters ? { filters } : {}),
        });
        return data?.attribute_names ?? [];
    }, []);

    const { data: customerOptions = [], isLoading: customersLoading } = useQuery({
        queryKey: ['validate-library-customers'],
        queryFn: () => fetchDropdownValues('customer_name'),
        enabled: open,
        staleTime: 60_000,
    });

    const { data: categoryOptions = [], isLoading: categoriesLoading } = useQuery({
        queryKey: ['validate-library-categories', selectedCustomer],
        queryFn: () => fetchDropdownValues('product_category', { dim1: [selectedCustomer] }),
        enabled: open && !!selectedCustomer,
        staleTime: 60_000,
    });

    const { mutate: triggerValidation, isPending: triggerLoading } = useMutation({
        mutationFn: async (payload: { customer_name: string; product_category: string; force?: boolean }) => {
            const { data } = await axios.post(tradePromoOptimizationUrls.validatePromoLibraryUrl, payload);
            return data as ValidatePromoLibraryResponse;
        },
        onSuccess: (data) => {
            setValidationId(data.validation_id);
            if (data.status === 'completed') {
                setPollingStatus(ValidatePromoLibraryPollingStatus.COMPLETED);
                setResultData(data);
            } else if (data.status === 'error') {
                setPollingStatus(ValidatePromoLibraryPollingStatus.ERROR);
                setResultData(data);
            } else {
                setPollingStatus(ValidatePromoLibraryPollingStatus.IN_PROGRESS);
            }
        },
        onError: () => {
            setPollingStatus(ValidatePromoLibraryPollingStatus.ERROR);
        },
    });

    useQuery({
        queryKey: ['validate-library-poll', validationId],
        queryFn: async () => {
            const { data } = await axios.get(
                `${tradePromoOptimizationUrls.getValidatePromoLibraryResult}/${validationId}`,
            );
            const response = data as ValidatePromoLibraryResponse;

            if (response.status === 'completed') {
                setPollingStatus(ValidatePromoLibraryPollingStatus.COMPLETED);
                setResultData(response);
            } else if (response.status === 'error') {
                setPollingStatus(ValidatePromoLibraryPollingStatus.ERROR);
                setResultData(response);
            }
            return response;
        },
        enabled: !!validationId && pollingStatus === ValidatePromoLibraryPollingStatus.IN_PROGRESS,
        refetchInterval:
            pollingStatus === ValidatePromoLibraryPollingStatus.IN_PROGRESS ? VALIDATE_PROMO_LIBRARY_POLLING_INTERVAL : false,
        staleTime: 0,
        retry: false,
    });

    const clearResults = useCallback(() => {
        setValidationId(null);
        setPollingStatus(ValidatePromoLibraryPollingStatus.NOT_STARTED);
        setResultData(null);
        setExpandAll(false);
        setActiveKeys([]);
    }, []);

    const handleValidate = () => {
        if (!selectedCustomer || !selectedCategory) return;
        clearResults();
        setPollingStatus(ValidatePromoLibraryPollingStatus.IN_PROGRESS);
        triggerValidation({ customer_name: selectedCustomer, product_category: selectedCategory });
    };

    const handleForceRefresh = () => {
        if (!selectedCustomer || !selectedCategory) return;
        clearResults();
        setPollingStatus(ValidatePromoLibraryPollingStatus.IN_PROGRESS);
        triggerValidation({ customer_name: selectedCustomer, product_category: selectedCategory, force: true });
    };

    const handleClose = () => {
        setSelectedCustomer(undefined);
        setSelectedCategory(undefined);
        clearResults();
        onClose();
    };

    const handleReset = () => {
        setSelectedCustomer(undefined);
        setSelectedCategory(undefined);
        clearResults();
    };

    const handleCustomerChange = (val: string | undefined) => {
        setSelectedCustomer(val);
        setSelectedCategory(undefined);
        clearResults();
    };

    const handleCategoryChange = (val: string | undefined) => {
        setSelectedCategory(val);
        clearResults();
    };

    const isLoading =
        pollingStatus === ValidatePromoLibraryPollingStatus.IN_PROGRESS || triggerLoading;
    const isCompleted = pollingStatus === ValidatePromoLibraryPollingStatus.COMPLETED && resultData?.data?.sections;
    const isError = pollingStatus === ValidatePromoLibraryPollingStatus.ERROR;

    const sections = useMemo(() => resultData?.data?.sections ?? [], [resultData]);
    const overallFlag = resultData?.data?.overall_flag;

    useEffect(() => {
        if (!sections.length) {
            setActiveKeys([]);
            return;
        }
        const redKeys = sections.filter((s) => s.flag === 'red').map((s) => s.section_key);
        setActiveKeys(redKeys.length > 0 ? redKeys : [sections[0].section_key]);
    }, [sections]);

    const loadingIcon = <LoadingOutlined style={{ fontSize: 32, color: colorConstants.violet }} spin />;

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-base">Validate Library for Conflicts</span>
                    <CloseOutlined onClick={handleClose} className="cursor-pointer text-gray-500 hover:text-gray-700" />
                </div>
            }
            placement="right"
            onClose={handleClose}
            open={open}
            width="65%"
            closable={false}
            bodyStyle={{ padding: '16px' }}
            headerStyle={{ borderBottom: 'none', padding: '16px 24px' }}
        >
            {/* Selection bar */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Select
                    placeholder="Select Customer"
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                    loading={customersLoading}
                    showSearch
                    filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={customerOptions.map((c: string) => ({ label: c, value: c }))}
                    style={{ width: 220 }}
                    allowClear
                    disabled={customersLoading || isLoading}
                    notFoundContent={customersLoading ? <Spin size="small" /> : undefined}
                />
                <Select
                    placeholder={selectedCustomer ? 'Select Category' : 'Select Customer first'}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    loading={categoriesLoading}
                    showSearch
                    filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={categoryOptions.map((c: string) => ({ label: c, value: c }))}
                    style={{ width: 220 }}
                    allowClear
                    disabled={!selectedCustomer || categoriesLoading || isLoading}
                    notFoundContent={categoriesLoading ? <Spin size="small" /> : undefined}
                />
                <Button
                    type={!selectedCustomer || !selectedCategory ? 'default' : 'primary'}
                    onClick={handleValidate}
                    disabled={!selectedCustomer || !selectedCategory || isLoading}
                    style={
                        !selectedCustomer || !selectedCategory
                            ? { background: '#f0f0f0', borderColor: '#d9d9d9', color: '#bfbfbf',  borderRadius:'6px'  }
                            : { background: colorConstants.violet, borderColor: colorConstants.violet, color: '#fff', borderRadius:'6px' }
                    }
                >
                    Validate
                </Button>
                {isCompleted && (
                    <Button
                        type="link"
                        onClick={handleForceRefresh}
                        disabled={isLoading}
                        style={{ color: colorConstants.violet, display: 'inline-flex', alignItems: 'center' }}
                        icon={<ReloadOutlined />}
                    >
                        Force Refresh
                    </Button>
                )}
                {(isCompleted || isError) && (
                    <Button type="link" onClick={handleReset} style={{ color: colorConstants.violet }}>
                        Reset
                    </Button>
                )}
            </div>

            {/* Subtitle + Legend */}
            {isCompleted && (
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <span className="bg-bgBackground rounded px-3 py-1 text-xs">
                            <span className="font-semibold text-gray-700 pr-1">Customer: </span>
                            <span className="text-purpleFont font-medium">{resultData?.data?.customer_name}</span>
                        </span>
                        <span className="bg-bgBackground rounded px-3 py-1 text-xs">
                            <span className="font-semibold text-gray-700 pr-1">Product Category: </span>
                            <span className="text-purpleFont font-medium">{resultData?.data?.product_category}</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <div style={{ width: 24, height: 8, background: FLAG_COLORS.green, borderRadius: 2 }} />
                            <span className="text-xs text-gray-500">Checks passed</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div style={{ width: 24, height: 8, background: FLAG_COLORS.red, borderRadius: 2 }} />
                            <span className="text-xs text-gray-500">Checks failed</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Overall flag + Expand All */}
            {isCompleted && overallFlag && (
                <div className="flex items-center justify-between mb-4 px-3 py-2 rounded-md" style={{ background: '#f5f5f5' }}>
                    <div className="flex items-center gap-2">
                        <FlagDot flag={overallFlag} />
                        <span className="text-sm font-medium">
                            Overall: {overallFlag === 'green' ? 'All checks passed' : 'Issues found'}
                        </span>
                    </div>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            if (expandAll) {
                                setActiveKeys([]);
                            } else {
                                setActiveKeys(sections.map((s) => s.section_key));
                            }
                            setExpandAll((prev) => !prev);
                        }}
                        style={{ color: colorConstants.violet, padding: 0 }}
                    >
                        {expandAll ? 'Collapse All' : 'Expand All'}
                    </Button>
                </div>
            )}

            {/* Loading state */}
            {isLoading && (
                <div className="flex flex-col items-center justify-center" style={{ minHeight: 300 }}>
                    <Spin indicator={loadingIcon} />
                    <span className="mt-4 text-sm text-gray-500">Validating library...</span>
                </div>
            )}

            {/* Error state */}
            {isError && (
                <div className="flex flex-col items-center justify-center text-center px-6" style={{ minHeight: 300 }}>
                    <span className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</span>
                    <span className="text-sm text-gray-500 mb-4">
                        {resultData?.error_message ?? 'Unable to validate the library. Please try again.'}
                    </span>
                    <Button
                        type="primary"
                        onClick={handleValidate}
                        style={{ background: colorConstants.violet, borderColor: colorConstants.violet }}
                    >
                        Retry
                    </Button>
                </div>
            )}

            {/* Results */}
            {isCompleted && sections.length > 0 && (
                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => {
                        const openKeys = Array.isArray(keys) ? keys as string[] : [keys as string];
                        setActiveKeys(openKeys);
                        setExpandAll(openKeys.length === sections.length);
                    }}
                    expandIconPosition="end"
                    bordered={false}
                    style={{ background: 'transparent' }}
                >
                    {sections.map((section) => (
                        <Panel
                            key={section.section_key}
                            header={
                                <div className="flex items-center">
                                    <FlagDot flag={section.flag} />
                                    <span className="font-semibold text-sm">{section.section_title}</span>
                                </div>
                            }
                            style={{
                                marginBottom: 12,
                                background: '#f5f5f5',
                                borderRadius: 8,
                                border: 'none',
                            }}
                        >
                            <SectionPanel section={section} />
                        </Panel>
                    ))}
                </Collapse>
            )}

            {/* Empty state */}
            {!isLoading && !isError && !isCompleted && pollingStatus === ValidatePromoLibraryPollingStatus.NOT_STARTED && (
                <div className="flex items-center justify-center text-sm text-gray-400" style={{ minHeight: 200 }}>
                    Select a customer and category, then click Validate.
                </div>
            )}
        </Drawer>
    );
};

export default ValidateLibraryDrawer;
